import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CAPABILITIES } from "@/data/content";
import { MaskedLines, Reveal } from "@/components/motion";
import { Divisions } from "@/components/Divisions";
import { CTABand } from "@/components/CTABand";
import { usePageMeta } from "@/lib/seo";

export default function BusinessSolutions() {
  usePageMeta(
    "Business Solutions — Iconicho, LLC",
    "Business consulting and management, leadership and professional development, marketing, administrative support, procurement and supply, logistics and delivery, and government contracting solutions from Iconicho, LLC."
  );
  return (
    <>
      <section className="grid-lines-light pt-36 lg:pt-44" data-testid="solutions-hero">
        <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs font-medium uppercase tracking-[0.3em] text-charcoal/60">
                What We Do
              </span>
            </div>
          </Reveal>
          <MaskedLines
            testId="solutions-headline"
            className="mt-8 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl"
            delay={0.15}
            lines={[
              "Solutions Structured",
              <span key="l2">Around the <span className="text-gold">Requirement.</span></span>,
            ]}
          />
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-charcoal/75">
              Iconicho delivers solutions across seven core categories. Every
              service can be tailored to organizational requirements, program
              needs, and defined contract scopes.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32" data-testid="solutions-list">
        <div>
          {CAPABILITIES.map((cap, i) => (
            <Reveal key={cap.title} delay={0.03 * i}>
              <div
                data-testid={`solution-row-${cap.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group grid gap-6 border-t border-charcoal/15 py-10 transition-colors duration-300 last:border-b hover:bg-white/60 lg:grid-cols-12 lg:items-center"
              >
                <div className="lg:col-span-1">
                  <span className="font-heading text-sm font-bold tracking-[0.25em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex items-center gap-5 lg:col-span-5">
                  <cap.icon
                    className="h-7 w-7 shrink-0 text-navy transition-colors duration-300 group-hover:text-gold"
                    strokeWidth={1.5}
                  />
                  <h2 className="font-heading text-xl font-bold tracking-tight text-navy sm:text-2xl">
                    {cap.title}
                  </h2>
                </div>
                <div className="lg:col-span-6">
                  <p className="max-w-xl leading-relaxed text-charcoal/70">
                    {cap.description}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col items-start justify-between gap-6 rounded-sm border border-charcoal/10 bg-white p-10 lg:flex-row lg:items-center">
            <p className="max-w-2xl font-heading text-lg font-bold leading-snug tracking-tight text-navy sm:text-xl">
              Have a defined scope or organizational requirement? Solutions are
              structured around your needs — not a fixed menu.
            </p>
            <Link
              to="/contact"
              data-testid="solutions-scope-cta"
              className="group inline-flex shrink-0 items-center gap-3 rounded-sm bg-navy px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-charcoal"
            >
              Discuss Your Requirement
              <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      <Divisions index="02" />

      <CTABand
        title="One Platform. Multiple Solutions."
        copy="From consulting to procurement to logistics — tell us what your organization needs to execute."
        testId="solutions-cta-band"
      />
    </>
  );
}
